"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useRef } from "react";
import WebBrainLeftSidebarWaves from "./WebBrainLeftSidebarWaves";
import WebDirectMessagesList from "./WebDirectMessagesList";
import MemesWaveFooter from "../waves/MemesWaveFooter";
import MemesQuickVoteDialog from "../waves/memes-quick-vote/MemesQuickVoteDialog";
import { usePathname } from "next/navigation";
import { useMemesQuickVoteDialogController } from "@/hooks/useMemesQuickVoteDialogController";
import { useSidebarState } from "../../../../hooks/useSidebarState";
import { ChevronDoubleRightIcon } from "@heroicons/react/24/outline";

/**
 * WebLeftSidebar
 *
 * Web‑specific left sidebar content. Chooses between Waves and Direct Messages
 * sections based on the current pathname ("/messages" routes show DMs).
 *
 * Rendering
 * - Uses an internal scroll container to enable inertia scrolling and custom
 *   scrollbars without managing explicit heights; relies on parent layout flex.
 */
interface WebLeftSidebarProps {
  readonly isCollapsed?: boolean | undefined;
}
const WebLeftSidebarQuickVoteOwner: React.FC<{
  readonly isCollapsed: boolean;
}> = t0 => {
  const $ = _c(11);
  const {
    isCollapsed
  } = t0;
  const {
    closeQuickVote,
    isQuickVoteOpen,
    openQuickVote,
    prefetchQuickVote,
    quickVoteSessionId
  } = useMemesQuickVoteDialogController();
  let t1;
  if ($[0] !== isCollapsed || $[1] !== openQuickVote || $[2] !== prefetchQuickVote) {
    t1 = <div className="tw-flex-shrink-0 tw-bg-black"><MemesWaveFooter collapsed={isCollapsed} onOpenQuickVote={openQuickVote} onPrefetchQuickVote={prefetchQuickVote} /></div>;
    $[0] = isCollapsed;
    $[1] = openQuickVote;
    $[2] = prefetchQuickVote;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== closeQuickVote || $[5] !== isQuickVoteOpen || $[6] !== quickVoteSessionId) {
    t2 = <MemesQuickVoteDialog isOpen={isQuickVoteOpen} sessionId={quickVoteSessionId} onClose={closeQuickVote} />;
    $[4] = closeQuickVote;
    $[5] = isQuickVoteOpen;
    $[6] = quickVoteSessionId;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== t1 || $[9] !== t2) {
    t3 = <>{t1}{t2}</>;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
};
const WebLeftSidebar: React.FC<WebLeftSidebarProps> = t0 => {
  const $ = _c(27);
  const {
    isCollapsed: t1
  } = t0;
  const isCollapsed = t1 === undefined ? false : t1;
  const scrollContainerRef = useRef(null);
  const pathname = usePathname();
  const {
    closeRightSidebar
  } = useSidebarState();
  let t2;
  if ($[0] !== pathname) {
    t2 = pathname.startsWith("/messages");
    $[0] = pathname;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const isMessagesView = t2;
  const expandLabel = isMessagesView ? "Expand messages panel" : "Expand waves panel";
  const t3 = `tw-relative tw-h-full tw-w-full ${isCollapsed ? "lg:tw-w-16" : "lg:tw-w-80"}`;
  const t4 = `tw-flex tw-h-full tw-w-full tw-flex-col tw-border-b-0 tw-border-l-0 tw-border-r tw-border-t-0 tw-border-solid tw-border-iron-700/95 ${isCollapsed ? "lg:tw-w-16" : "lg:tw-w-80"}`;
  let t5;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      minHeight: "100%"
    };
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  let t6;
  if ($[3] !== closeRightSidebar || $[4] !== expandLabel || $[5] !== isCollapsed) {
    t6 = isCollapsed && <div className="tw-sticky tw-top-2 tw-z-20 tw-flex tw-justify-center tw-py-2"><button type="button" onClick={closeRightSidebar} onMouseDown={_temp} aria-label={expandLabel} title={expandLabel} className="tw-flex tw-h-6 tw-w-6 tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-800 tw-shadow-[0_12px_28px_rgba(0,0,0,0.35)] desktop-hover:hover:tw-border-iron-600 desktop-hover:hover:tw-bg-iron-700 desktop-hover:hover:tw-shadow-[0_16px_34px_rgba(0,0,0,0.4)]"><ChevronDoubleRightIcon strokeWidth={2} className="tw-h-4 tw-w-4 tw-flex-shrink-0 tw-text-iron-200 tw-transition-all tw-duration-300 tw-ease-in-out group-hover:desktop-hover:hover:tw-text-white" /></button></div>;
    $[3] = closeRightSidebar;
    $[4] = expandLabel;
    $[5] = isCollapsed;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== isCollapsed || $[8] !== isMessagesView) {
    t7 = !isMessagesView && <WebBrainLeftSidebarWaves scrollContainerRef={scrollContainerRef} isCollapsed={isCollapsed} />;
    $[7] = isCollapsed;
    $[8] = isMessagesView;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== isCollapsed || $[11] !== isMessagesView) {
    t8 = isMessagesView && <WebDirectMessagesList scrollContainerRef={scrollContainerRef} isCollapsed={isCollapsed} />;
    $[10] = isCollapsed;
    $[11] = isMessagesView;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t6 || $[14] !== t7 || $[15] !== t8) {
    t9 = <div ref={scrollContainerRef} className="no-scrollbar tw-z-40 tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-overflow-y-auto tw-overflow-x-hidden tw-transition-colors tw-duration-500 tw-scrollbar-thin tw-scrollbar-track-iron-800 tw-scrollbar-thumb-iron-500 hover:tw-scrollbar-thumb-iron-300">{t6}{t7}{t8}</div>;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== isCollapsed || $[18] !== isMessagesView) {
    t10 = !isMessagesView && <WebLeftSidebarQuickVoteOwner isCollapsed={isCollapsed} />;
    $[17] = isCollapsed;
    $[18] = isMessagesView;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== t10 || $[21] !== t4 || $[22] !== t9) {
    t11 = <div className={t4} style={t5}>{t9}{t10}</div>;
    $[20] = t10;
    $[21] = t4;
    $[22] = t9;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t11 || $[25] !== t3) {
    t12 = <div className={t3}>{t11}</div>;
    $[24] = t11;
    $[25] = t3;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  return t12;
};
export default WebLeftSidebar;
function _temp(event) {
  return event.preventDefault();
}